import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'motion/react'
import { Button } from '@/components/ui'
import { Check } from 'lucide-react'
import {
  CAPABILITIES,
  MANAGED_SERVICES,
  DFY_BUNDLES,
  FESTIVAL_LANE,
  ONE_TIME_AND_ADDONS,
  bundlePriceMonthly,
} from '@gjl/voice/pricing'

// Interactive two-axis pricing: which capabilities, and who runs them.
// All numbers come from @gjl/voice/pricing; the only logic here is composition.

const launchBuild = ONE_TIME_AND_ADDONS.find((p) => p.id === 'launch-build')!
const websiteCare = MANAGED_SERVICES.find((s) => s.id === 'website-care')!
const managedWebsite = MANAGED_SERVICES.find((s) => s.id === 'managed-website')!
const managedEmail = MANAGED_SERVICES.find((s) => s.id === 'managed-email')!
const growthPartner = DFY_BUNDLES.find((b) => b.id === 'growth-partner')!
const concierge = DFY_BUNDLES.find((b) => b.id === 'concierge')!
const festivalPlatform = FESTIVAL_LANE.find((f) => f.id === 'festival-platform')!
type Mode = 'diy' | 'help'
type WebsiteLevel = 'care' | 'managed'

interface LineItem {
  label: string
  amount: string
}

// PriceBundle prices are display strings ('$500/mo'); derive numbers for math.
const growthPartnerMonthly = Number(growthPartner.price.replace(/[^0-9]/g, ''))
const festivalPlatformMonthly = Number(festivalPlatform.price.replace(/[^0-9]/g, ''))

const ONE_TIME_AMOUNT = launchBuild.price.replace(' one-time', '')
const ONE_TIME_LINE: LineItem = {
  label: `${launchBuild.name}: custom design, your content in, domain connected. Doubles as onboarding for done-for-you plans.`,
  amount: ONE_TIME_AMOUNT,
}

const CAPABILITY_OPTIONS = CAPABILITIES.map((c) => ({ id: c.id, label: c.name }))
const APPLICATIONS_ID = 'applications'

export function PricingConfigurator() {
  const [selected, setSelected] = useState<Set<string>>(new Set(['website']))
  const [mode, setMode] = useState<Mode>('diy')
  const [websiteLevel, setWebsiteLevel] = useState<WebsiteLevel>('care')

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const result = useMemo(() => {
    const items: LineItem[] = []
    const notes: string[] = []
    let monthly = 0
    // When set, this range replaces the computed sum as the headline total
    // (Concierge engagements are a range, not a sum of parts).
    let totalDisplay: string | null = null

    const wantsApplications = selected.has(APPLICATIONS_ID)
    const capIds = [...selected].filter((id) => id !== APPLICATIONS_ID)

    if (selected.size === 0) {
      return { items, notes, monthly, totalDisplay, oneTime: null as LineItem | null }
    }

    if (wantsApplications) {
      if (mode === 'help') {
        // A festival that wants it all run for them is a Concierge engagement.
        items.push({
          label: `${concierge.name}: the festival fully run (Festival Platform + branding + launch + strategy)`,
          amount: concierge.price,
        })
        totalDisplay = concierge.price
        notes.push('Everything you selected is included: giving, Music Manager, the lot. One engagement, one number.')
        return { items, notes, monthly, totalDisplay, oneTime: ONE_TIME_LINE }
      }
      // DIY festival: the Festival Platform lane (light-managed by our team).
      monthly += festivalPlatformMonthly
      items.push({
        label: `${festivalPlatform.name} (website, applications, email, ticketing, light-managed)`,
        amount: festivalPlatform.price,
      })
      const covered = new Set(['website', 'email', 'ticketing'])
      const extras = capIds.filter((id) => !covered.has(id))
      for (const id of extras) {
        const cap = CAPABILITIES.find((c) => c.id === id)!
        monthly += cap.priceMonthly
        items.push({ label: cap.name, amount: `$${cap.priceMonthly}/mo` })
      }
      notes.push('Light-managed: you run the day to day, our team keeps it running underneath.')
      return { items, notes, monthly, totalDisplay, oneTime: ONE_TIME_LINE }
    }

    if (mode === 'diy') {
      const price = bundlePriceMonthly(capIds.length)
      monthly = price
      const names = capIds.map((id) => CAPABILITIES.find((c) => c.id === id)!.name).join(' + ')
      items.push({ label: names || 'Capabilities', amount: `$${price}/mo` })
      const saved = capIds.length * 25 - price
      if (saved > 0) notes.push(`Bundled: $${saved}/mo under a la carte.`)
      const teamRun = capIds.filter((id) => !CAPABILITIES.find((c) => c.id === id)!.selfServeLive)
      if (teamRun.length > 0) {
        notes.push(
          'Donor management, ticketing, and Music Manager are set up and run for you by our team today, included in the capability price.'
        )
      }
      return { items, notes, monthly, totalDisplay, oneTime: ONE_TIME_LINE }
    }

    // Help mode: managed services per capability.
    const helped = new Set(capIds)
    if (helped.size >= 4) {
      // At this scope, summing managed services stops being honest: this is a
      // Concierge engagement, so the range IS the price.
      items.push({
        label: `${concierge.name}: everything run for you, plus strategy`,
        amount: concierge.price,
      })
      totalDisplay = concierge.price
      notes.push('Covers every capability you selected, fundraising and box office included.')
      notes.push(
        'Done-for-you engagements start with a one-time onboarding (the $250 Launch build counts) and a 3-month minimum.'
      )
      return { items, notes, monthly, totalDisplay, oneTime: ONE_TIME_LINE }
    }
    const websiteManaged = helped.has('website') && websiteLevel === 'managed'
    if (websiteManaged && helped.has('email')) {
      monthly += growthPartnerMonthly
      items.push({
        label: `${growthPartner.name} (${managedWebsite.name} + ${managedEmail.name})`,
        amount: growthPartner.price,
      })
      helped.delete('website')
      helped.delete('email')
    }
    if (helped.has('website')) {
      const svc = websiteLevel === 'care' ? websiteCare : managedWebsite
      monthly += svc.priceMonthly
      items.push({ label: svc.name, amount: `$${svc.priceMonthly}/mo` })
      helped.delete('website')
    }
    if (helped.has('email')) {
      monthly += managedEmail.priceMonthly
      items.push({ label: managedEmail.name, amount: `$${managedEmail.priceMonthly}/mo` })
      helped.delete('email')
    }
    for (const id of helped) {
      const cap = CAPABILITIES.find((c) => c.id === id)!
      monthly += cap.priceMonthly
      items.push({ label: `${cap.name} (set up and run by our team)`, amount: `$${cap.priceMonthly}/mo` })
    }
    if (capIds.includes('donor-management') || capIds.includes('ticketing')) {
      notes.push(`Full fundraising or box-office execution lives in ${concierge.name} (${concierge.price}).`)
    }
    notes.push(
      'Done-for-you plans start with a one-time onboarding (the $250 Launch build counts) and a 3-month minimum.'
    )
    return { items, notes, monthly, totalDisplay, oneTime: ONE_TIME_LINE }
  }, [selected, mode, websiteLevel])

  return (
    <section className="mx-auto max-w-4xl px-8">
      <div className="border-primary/40 bg-gray-900/40 rounded-2xl border p-8">
        <h2 className="text-center text-2xl font-bold">Price it in ten seconds</h2>
        <p className="text-muted-foreground mt-2 text-center text-sm">
          Pick what you need and who runs it. Every price here is the real price.
        </p>

        {/* Step 1: capabilities */}
        <div className="mt-8">
          <div className="text-sm font-semibold text-white/90">1. What do you need?</div>
          <div className="mt-3 flex flex-wrap gap-2">
            {CAPABILITY_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => toggle(opt.id)}
                aria-pressed={selected.has(opt.id)}
                className={`cursor-pointer rounded-full border px-4 py-2 text-sm transition ${
                  selected.has(opt.id)
                    ? 'border-primary bg-primary/20 text-white'
                    : 'border-border text-muted-foreground hover:border-primary/50'
                }`}
              >
                {selected.has(opt.id) && <Check className="mr-1 inline h-3.5 w-3.5" strokeWidth={3} />}
                {opt.label}
              </button>
            ))}
            <button
              type="button"
              onClick={() => toggle(APPLICATIONS_ID)}
              aria-pressed={selected.has(APPLICATIONS_ID)}
              className={`cursor-pointer rounded-full border px-4 py-2 text-sm transition ${
                selected.has(APPLICATIONS_ID)
                  ? 'border-primary bg-primary/20 text-white'
                  : 'border-border text-muted-foreground hover:border-primary/50'
              }`}
            >
              {selected.has(APPLICATIONS_ID) && <Check className="mr-1 inline h-3.5 w-3.5" strokeWidth={3} />}
              Auditions &amp; Applications (festivals)
            </button>
          </div>
        </div>

        {/* Step 2: who runs it */}
        <div className="mt-6">
          <div className="text-sm font-semibold text-white/90">2. Who runs it?</div>
          <div className="mt-3 flex gap-2">
            {(
              [
                { value: 'diy', label: 'I will run it' },
                { value: 'help', label: 'Run it for me' },
              ] as { value: Mode; label: string }[]
            ).map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setMode(opt.value)}
                aria-pressed={mode === opt.value}
                className={`cursor-pointer rounded-full border px-4 py-2 text-sm transition ${
                  mode === opt.value
                    ? 'border-primary bg-primary/20 text-white'
                    : 'border-border text-muted-foreground hover:border-primary/50'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {mode === 'help' && selected.has('website') && !selected.has(APPLICATIONS_ID) && (
            <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
              <span className="text-muted-foreground">Website support:</span>
              {(
                [
                  { value: 'care', label: `One update a month ($${websiteCare.priceMonthly})` },
                  { value: 'managed', label: `Unlimited updates ($${managedWebsite.priceMonthly})` },
                ] as { value: WebsiteLevel; label: string }[]
              ).map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setWebsiteLevel(opt.value)}
                  aria-pressed={websiteLevel === opt.value}
                  className={`cursor-pointer rounded-full border px-3 py-1.5 transition ${
                    websiteLevel === opt.value
                      ? 'border-primary bg-primary/20 text-white'
                      : 'border-border text-muted-foreground hover:border-primary/50'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Result */}
        <motion.div
          layout
          className="border-border mt-8 rounded-xl border bg-black/30 p-6"
          transition={{ type: 'spring', stiffness: 300, damping: 25 }}
        >
          {result.items.length === 0 ? (
            <p className="text-muted-foreground text-center text-sm">Pick at least one capability above.</p>
          ) : (
            <>
              <div className="text-xs font-semibold tracking-wide text-white/50 uppercase">Ongoing monthly</div>
              <ul className="mt-2 space-y-2 text-sm">
                {result.items.map((item) => (
                  <li key={item.label} className="flex items-baseline justify-between gap-4">
                    <span className="text-white/90">{item.label}</span>
                    <span className="text-primary font-semibold whitespace-nowrap">{item.amount}</span>
                  </li>
                ))}
              </ul>
              <div className="border-border mt-4 flex items-baseline justify-between border-t pt-4">
                <span className="font-semibold text-white">Your monthly</span>
                <span className="text-primary text-3xl font-bold">
                  {result.totalDisplay ?? `$${result.monthly}/mo`}
                </span>
              </div>
              {result.oneTime && (
                <div className="border-border mt-5 border-t pt-4">
                  <div className="text-xs font-semibold tracking-wide text-white/50 uppercase">
                    One-time setup (not monthly)
                  </div>
                  <div className="mt-2 flex items-baseline justify-between gap-4 text-sm">
                    <span className="text-white/90">{result.oneTime.label}</span>
                    <span className="text-primary font-semibold whitespace-nowrap">{result.oneTime.amount} once</span>
                  </div>
                </div>
              )}
              {result.notes.map((note) => (
                <p key={note} className="text-muted-foreground mt-2 text-xs">
                  {note}
                </p>
              ))}
              <p className="text-muted-foreground mt-2 text-xs">Pay annually and get 2 months free.</p>
              <div className="mt-5 flex flex-col justify-center gap-3 sm:flex-row">
                <Link to="/get-started">
                  <Button className="w-full cursor-pointer sm:w-auto">Get Started</Button>
                </Link>
                <a href="/book" target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="w-full cursor-pointer sm:w-auto">
                    Book a Free Call
                  </Button>
                </a>
              </div>
            </>
          )}
        </motion.div>
      </div>
    </section>
  )
}
